import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import {
  useTranslation,
  translateHeaders,
} from "../../../utils/useTranslation";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { useAppTheme } from "../../../hooks/useAppTheme";
import {
  Provider as PaperProvider,
  DarkTheme,
  DefaultTheme,
  DataTable,
} from "react-native-paper";
import { DatePickerModal } from "react-native-paper-dates";
import { getDeliveryAttachmentsList } from "../../../redux/dispatcher/deliveryDispatcher";
import {
  updateAttachmentsPage,
  searchAttachments,
  updateAttachmentsSort,
} from "../../../redux/reducers/deliveryReducer";
import Spinner from "../../../components/shared/Spinner";

interface DeliveryRecentUploadsModalProps {
  visible: boolean;
  onClose: () => void;
  onRowClick?: (row: any) => void;
  deliveryList: any[];
}

const headers = {
  docNum: {
    name: "Document No",
    width: 0.3,
  },
  attachmentName: {
    name: "Attachment",
    width: 0.3,
  },
  remarks: {
    name: "Remarks",
    width: 0.3,
  },
  createdAt: {
    name: "Created Date",
    width: 0.4,
  },
};

const recentUploadsTranslationMap: Record<string, any> = {
  docNum: "documentNo",
  attachmentName: "attachments",
  remarks: "remarks",
  createdAt: "createdDate",
};

const truncateName = (name: string, max = 10) => {
  if (!name) return "-";
  return name.length > max ? `${name.slice(0, max)}...` : name;
};

const getFileDisplayName = (files: string[] = []) => {
  if (!files.length) return "-";
  const raw = files[0].split("/").pop() || files[0];
  return truncateName(raw, 10);
};

const DeliveryRecentUploadsModal: React.FC<DeliveryRecentUploadsModalProps> = ({
  visible,
  onClose,
  onRowClick,
  deliveryList,
}) => {
  const listRef = useRef<ScrollView>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  const { isLargeDevice, isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );

  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);

  const {
    attachmentsList,
    loadingAttachments,
    attachmentsPage,
    attachmentsPageSize,
    attachmentsTotalRows,
    attachmentsTotalPages,
    attachmentsSort,
    attachmentsSearch,
  } = useSelector((state: any) => state.delivery);

  const [sortDirection, setSortDirection] = useState<"recent" | "oldest">(
    "recent",
  );
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({ startDate: undefined, endDate: undefined });

  const [dateFilterPreset, setDateFilterPreset] = useState<
    "all" | "last7days" | "yesterday" | "today" | "custom"
  >("all");
  const [filterOpen, setFilterOpen] = useState<"date" | null>(null);
  const dateFilters = ["all", "today", "yesterday", "last7days", "custom"];

  const fetchData = () => {
    dispatch(
      getDeliveryAttachmentsList({
        page: attachmentsPage,
        pageSize: attachmentsPageSize,
        sort: attachmentsSort,
        search: attachmentsSearch,
      }),
    );
  };

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [
    visible,
    attachmentsPage,
    attachmentsPageSize,
    attachmentsSearch,
    attachmentsSort,
  ]);

  useEffect(() => {
    dispatch(
      updateAttachmentsSort(
        sortDirection === "recent" ? "-createdAt" : "createdAt",
      ),
    );
  }, [sortDirection]);

  const formatShortDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateRangeLabel = () => {
    switch (dateFilterPreset) {
      case "all":
        return t("dateAll") || "Date: All";
      case "last7days":
        return t("last7days") || "Last 7 Days";
      case "yesterday":
        return t("yesterday") || "Yesterday";
      case "today":
        return t("today") || "Today";
      case "custom":
        if (customDateRange.startDate && customDateRange.endDate) {
          return `${formatShortDate(customDateRange.startDate)} ${t("to") || "to"} ${formatShortDate(customDateRange.endDate)}`;
        }
        return t("dateRange") || "Date Range";
      default:
        return t("dateAll") || "Date: All";
    }
  };

  const formattedUploads = useMemo(() => {
    let result = (attachmentsList || []).map((u: any) => {
      const deliveryItem = deliveryList?.find(
        (i: any) => String(i.DocNum) === String(u.docNum),
      );
      return {
        docNum: u.docNum,
        attachmentName: getFileDisplayName(u.files),
        remarks: u.remarks || "-",
        createdAt: u.createdAt || u.updatedAt,
        updatedAt: u.updatedAt,
        state: deliveryItem?.State || "-",
      };
    });

    if (dateFilterPreset !== "all") {
      result = result.filter((u: any) => {
        const itemDateStr = u.createdAt || u.updatedAt;
        if (!itemDateStr) return false;
        const itemDate = new Date(itemDateStr);
        itemDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateFilterPreset === "today") {
          return itemDate.getTime() === today.getTime();
        }
        if (dateFilterPreset === "yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        }
        if (dateFilterPreset === "last7days") {
          const last7 = new Date(today);
          last7.setDate(last7.getDate() - 7);
          return itemDate >= last7 && itemDate <= today;
        }
        if (
          dateFilterPreset === "custom" &&
          customDateRange.startDate &&
          customDateRange.endDate
        ) {
          return (
            itemDate >= customDateRange.startDate! &&
            itemDate <= customDateRange.endDate!
          );
        }
        return true;
      });
    }

    return result;
  }, [
    attachmentsList,
    deliveryList,
    dateFilterPreset,
    customDateRange.startDate,
    customDateRange.endDate,
  ]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback(
    (text: string) => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        const cleanText = text.replace(/"/g, "").trim();
        dispatch(searchAttachments(cleanText));
      }, 400);
    },
    [dispatch],
  );

  // clear any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  const translatedHeaders = useMemo(
    () => translateHeaders(headers, t, recentUploadsTranslationMap),
    [t],
  );

  const tableContent = (
    <View>
      {loadingAttachments ? (
        <View>
          <Spinner
            size="small"
            color="#74BBFF"
            styles={{ flexDirection: "row", margin: "20%" }}
          />
        </View>
      ) : (
        <DataTableWithPagination
          isToExpandAllRows={false}
          isRowsExpandable={false}
          headers={translatedHeaders}
          rows={formattedUploads}
          itemsPerPage={attachmentsPageSize}
          isDisplayTimeWithDate={true}
          useModal={true}
          isDetail={false}
          isEdit={false}
          isDelete={false}
          itemTracker={false}
          setModalVisible={() => {}}
          handleRowClick={(row) => onRowClick && onRowClick(row)}
          setSelected={() => {}}
          handleDeleteRow={() => {}}
          page={attachmentsPage - 1}
          setPage={(p: number) => dispatch(updateAttachmentsPage(p + 1))}
          handleSortColumn={() => {}}
          operationActions={(row) => onRowClick && onRowClick(row)}
          rowKeyValue="docNum"
          columnsToNotHaveThousandSeparator={[
            "Document No",
            "docNum",
            "Request Date",
            t("documentNo"),
          ]}
          hidePagination={true}
        />
      )}
    </View>
  );

  const titleAndSearch = (
    <View
      style={[
        styles.titleContainer,
        isSmallDevice && { flexDirection: "column" },
      ]}
    >
      <Text style={[styles.titleText, isSmallDevice && styles.titleTextSmall]}>
        {t("recentUploads") || "Recent Uploads"} (
        {attachmentsTotalRows ?? formattedUploads.length})
      </Text>
      <View style={styles.searchContainer}>
        <LabelBannerWithSearchBar
          titleName={``}
          handleSearch={handleSearch}
          setRefresh={fetchData}
          dataToExport={formattedUploads}
          fileName="RecentUploads"
          sheetName="Recent Uploads"
        />
      </View>
    </View>
  );

  const filterBar = (
    <View style={styles.filterBarContainer}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterBarContent}
      >
        <TouchableOpacity onPress={() => setSortDirection("recent")}>
          <Text
            style={
              sortDirection === "recent"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↓ {t("recent")}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setSortDirection("oldest")}>
          <Text
            style={
              sortDirection === "oldest"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↑ {t("oldest")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => setFilterOpen(filterOpen === "date" ? null : "date")}
        >
          <Text
            style={
              dateFilterPreset !== "all"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            {getDateRangeLabel()} {filterOpen === "date" ? "▲" : "▼"}
          </Text>
        </TouchableOpacity>
        {dateFilterPreset === "custom" &&
          customDateRange.startDate &&
          customDateRange.endDate && (
            <TouchableOpacity
              onPress={() => {
                setCustomDateRange({
                  startDate: undefined,
                  endDate: undefined,
                });
                setDateFilterPreset("all");
              }}
            >
              <Text style={styles.filterLabel}>✕</Text>
            </TouchableOpacity>
          )}
      </ScrollView>

      {filterOpen === "date" && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.filterBarContent, { marginTop: 14 }]}
        >
          {dateFilters.map((df) => {
            const isActive = dateFilterPreset === df;
            let label = t("all") || "All";
            if (df === "today") label = t("today") || "Today";
            if (df === "yesterday") label = t("yesterday") || "Yesterday";
            if (df === "last7days") label = t("last7days") || "Last 7 Days";
            if (df === "custom") label = t("dateRange") || "Date Range";

            return (
              <TouchableOpacity
                key={df}
                onPress={() => {
                  if (df === "custom") {
                    setDateRangePickerOpen(true);
                  }
                  setDateFilterPreset(df as any);
                }}
              >
                <Text
                  style={isActive ? styles.selectedFilter : styles.filterLabel}
                >
                  {label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );

  const pagination =
    (attachmentsTotalRows ?? 0) > 0 ? (
      <DataTable.Pagination
        page={attachmentsPage - 1}
        numberOfPages={attachmentsTotalPages ?? 1}
        onPageChange={(p) => dispatch(updateAttachmentsPage(p + 1))}
        label={`${Math.min(attachmentsPage * attachmentsPageSize, attachmentsTotalRows ?? 0)} ${t("of")} ${attachmentsTotalRows ?? 0}`}
        showFastPaginationControls
        numberOfItemsPerPage={attachmentsPageSize}
        style={{ alignSelf: "flex-end", marginRight: 16 }}
      />
    ) : null;

  const footer = (
    <View style={[styles.footer, isSmallDevice && styles.footerSmall]}>
      <TouchableOpacity
        onPress={onClose}
        style={[styles.closeButton, isSmallDevice && styles.buttonSmall]}
      >
        <Text
          style={[
            styles.closeButtonText,
            isSmallDevice && styles.buttonTextSmall,
          ]}
        >
          {t("close")}
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <>
      <Modal
        transparent={true}
        visible={visible}
        animationType="fade"
        onRequestClose={onClose}
      >
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: "100%", height: "100%" },
                ]}
              >
                {titleAndSearch}
                {filterBar}
                <ScrollView ref={listRef}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{ minWidth: "100%" }}
                  >
                    <View
                      style={[styles.containerContent, { minWidth: "100%" }]}
                    >
                      {tableContent}
                    </View>
                  </ScrollView>
                </ScrollView>
                {pagination}
                {footer}
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%", height: "90%" },
              ]}
            >
              {titleAndSearch}
              {filterBar}
              <ScrollView ref={listRef}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{ minWidth: "100%" }}
                >
                  <View style={[styles.containerContent, { minWidth: "100%" }]}>
                    {tableContent}
                  </View>
                </ScrollView>
              </ScrollView>
              {pagination}
              {footer}
            </View>
          )}
        </View>
      </Modal>

      {/* @ts-ignore: DatePickerModal typing conflict */}
      <PaperProvider
        theme={
          isDark
            ? {
                ...DarkTheme,
                colors: { ...DarkTheme.colors, primary: colors.primary },
              }
            : {
                ...DefaultTheme,
                colors: { ...DefaultTheme.colors, primary: colors.primary },
              }
        }
      >
        <DatePickerModal
          locale="en-AU"
          mode="range"
          visible={dateRangePickerOpen}
          onDismiss={() => setDateRangePickerOpen(false)}
          startDate={customDateRange.startDate}
          endDate={customDateRange.endDate}
          onConfirm={({ startDate, endDate }: any) => {
            setDateRangePickerOpen(false);
            const finalEndDate = endDate || startDate;
            if (startDate && finalEndDate) {
              startDate.setHours(0, 0, 0, 0);
              finalEndDate.setHours(0, 0, 0, 0);
              setCustomDateRange({ startDate, endDate: finalEndDate });
            }
          }}
        />
      </PaperProvider>
    </>
  );
};

const createStyles = (colors: any, isDark: boolean) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0,0,0,0.45)",
      paddingVertical: 16,
      paddingHorizontal: 8,
    },
    modalContainer: {
      backgroundColor: colors.surface,
      padding: 20,
      elevation: 12,
      borderRadius: 12,
      width: "100%",
      maxHeight: "95%",
    },
    modalContainerSecond: {
      backgroundColor: colors.surface,
      elevation: 12,
      borderRadius: 12,
      padding: 16,
      width: "100%",
      height: "95%",
    },
    titleContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle || "#E5E7EB",
      backgroundColor: colors.surface,
      paddingVertical: 12,
      marginBottom: 12,
    },
    titleText: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
    },
    titleTextSmall: {
      fontSize: 14,
    },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
    },
    filterBarContainer: {
      marginBottom: 10,
      paddingHorizontal: 4,
    },
    filterBarContent: {
      flexDirection: "row",
      alignItems: "center",
    },
    filterLabel: {
      fontSize: 12,
      fontWeight: "500",
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
      marginHorizontal: 4,
      backgroundColor: isDark ? colors.surfaceSubtle : "#F1F5F9",
      color: isDark ? colors.textSecondary : "#4B5563",
      overflow: "hidden",
    },
    selectedFilter: {
      fontSize: 12,
      fontWeight: "600",
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
      marginHorizontal: 4,
      backgroundColor: colors.primary,
      color: "#fff",
      overflow: "hidden",
    },
    containerContent: {
      flexDirection: "column",
      alignSelf: "flex-start",
      width: "100%",
      paddingHorizontal: 0,
      paddingBottom: 0,
    },
    footer: {
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      marginTop: 20,
      padding: 25,
    },
    footerSmall: {
      flexDirection: "row",
      justifyContent: "flex-start",
    },
    closeButton: {
      padding: 10,
      borderWidth: 1,
      borderColor: colors.borderSubtle || "#ccc",
      borderRadius: 5,
      width: 120,
      alignItems: "center",
    },
    closeButtonText: {
      color: colors.text,
      fontSize: 14,
      fontWeight: "500",
    },
    buttonSmall: {
      padding: 8,
      paddingHorizontal: 12,
    },
    buttonTextSmall: {
      fontSize: 12,
    },
  });

export default DeliveryRecentUploadsModal;
